/**
 * SessionDiagram — front-view silhouette that heat-maps the muscles loaded
 * in the in-progress session. Ported from gymdash's BodyDiagramStatic, with
 * back view and breakdown <details> stripped — the chat panel column is
 * narrow and the panel already shows the prescription list + set log.
 *
 * SVG asset comes from /assets/diagram/front.svg (served by Bun directly,
 * not the React bundler). muscle-map.json maps each muscle_id to the
 * path-indices in the SVG that belong to that muscle. We fetch the SVG
 * text once, inject via dangerouslySetInnerHTML, tag every path with its
 * full-document index + classification, then re-fill on activation change.
 *
 * Palette mirrors bilbo's sacred red radial gradient — intensity goes from
 * the muscle-fill grey through oklch reds into the brightest accent.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import muscleMapJson from './assets/muscle-map.json';

export interface MuscleRow {
  muscleId: string;
  name: string;
  setsTotal: number;
  setsPrimary: number;
  setsSecondary: number;
  tonnageCurrent: number;
  tonnagePeak: number;
  ratio: number;
}

interface Props {
  muscles: MuscleRow[];
}

const MUSCLE_INDICES = (muscleMapJson as {
  muscleIndices: Record<string, Record<string, number[]>>;
}).muscleIndices.front ?? {};

const FRONT_URL = '/assets/diagram/front.svg';
// Only paths whose original fill matches MUSCLE_FILL are "muscle" paths.
// Skin, strokes, and silhouette outlines stay untouched — filling those
// would cover every muscle underneath them.
const MUSCLE_FILL = '#BDBDBD';
// Intensity ramp — index 0 = no activity (source grey), 4 = peak. The
// darker steps lean to oklch reds so the diagram reads as "this session
// loaded this muscle hard" without colliding with the chat-panel chrome.
// Brightest end matches the radial-gradient accent the rest of bilbo uses.
const INTENSITY_FILLS = [MUSCLE_FILL, '#3a1a1a', '#7c2222', '#b91c1c', '#ef4444'];

function intensityFor(ratio: number): number {
  if (ratio <= 0) return 0;
  if (ratio < 0.2) return 1;
  if (ratio < 0.45) return 2;
  if (ratio < 0.75) return 3;
  return 4;
}

export function SessionDiagram({ muscles }: Props) {
  const [svgText, setSvgText] = useState<string | null>(null);
  const hostRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let cancelled = false;
    fetch(FRONT_URL)
      .then((r) => r.text())
      .then((t) => {
        if (!cancelled) setSvgText(t);
      })
      .catch(() => {
        if (!cancelled) setSvgText(null);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const byMuscle = useMemo(() => {
    const m = new Map<string, MuscleRow>();
    for (const r of muscles) m.set(r.muscleId, r);
    return m;
  }, [muscles]);

  // Stable wrapper so dangerouslySetInnerHTML doesn't wipe the data-* attrs
  // we set on each path in the classification effect.
  const innerHtml = useMemo(
    () => (svgText ? { __html: svgText } : undefined),
    [svgText],
  );

  // Classify paths once per SVG load — tag each path with its full-document
  // index, mark muscle vs other based on its original fill.
  useEffect(() => {
    const host = hostRef.current;
    if (!host || !svgText) return;
    const paths = host.querySelectorAll<SVGPathElement>('path');
    paths.forEach((p, idx) => {
      p.dataset.pathIndex = String(idx);
      const orig = (p.getAttribute('fill') ?? '').toUpperCase();
      p.dataset.kind = orig === MUSCLE_FILL.toUpperCase() ? 'muscle' : 'other';
    });
  }, [svgText]);

  // Apply per-muscle fills + tooltips whenever activation changes.
  useEffect(() => {
    const host = hostRef.current;
    if (!host || !svgText) return;
    const paths = host.querySelectorAll<SVGPathElement>('path[data-kind="muscle"]');

    // Compute target fill per path-index. If multiple muscles share a path,
    // higher-intensity assignments win — same precedence gymdash uses.
    const indexToFill = new Map<
      number,
      { fill: string; muscleId: string; row: MuscleRow | null }
    >();
    for (const [muscleId, indices] of Object.entries(MUSCLE_INDICES)) {
      const row = byMuscle.get(muscleId) ?? null;
      const lvl = intensityFor(row?.ratio ?? 0);
      const fill = INTENSITY_FILLS[lvl] ?? INTENSITY_FILLS[0]!;
      for (const idx of indices) {
        const existing = indexToFill.get(idx);
        const existingRow = existing ? byMuscle.get(existing.muscleId) ?? null : null;
        const existingLvl = intensityFor(existingRow?.ratio ?? 0);
        if (!existing || lvl > existingLvl) {
          indexToFill.set(idx, { fill, muscleId, row });
        }
      }
    }

    paths.forEach((p) => {
      const idxStr = p.dataset.pathIndex;
      if (idxStr == null) return;
      const idx = Number(idxStr);
      const t = indexToFill.get(idx);
      const fill = t?.fill ?? INTENSITY_FILLS[0]!;
      p.setAttribute('fill', fill);
      const prev = p.querySelector('title');
      if (prev) prev.remove();
      if (t?.row) {
        const title = document.createElementNS('http://www.w3.org/2000/svg', 'title');
        title.textContent =
          `${t.row.name} — ${Math.round(t.row.ratio * 100)}% of peak ` +
          `· ${t.row.tonnageCurrent.toLocaleString()} kg / ${t.row.tonnagePeak.toLocaleString()} kg ` +
          `· ${t.row.setsTotal} sets`;
        p.appendChild(title);
      }
    });
  }, [svgText, byMuscle]);

  if (muscles.length === 0) {
    return <div className="session-diagram session-diagram-empty">no muscle data yet</div>;
  }

  return (
    <div className="session-diagram">
      <div ref={hostRef} className="session-diagram-svg" dangerouslySetInnerHTML={innerHtml} />
    </div>
  );
}
